import { Accordion, Checkbox, DatePicker, FormControlLabel, Text, TextField, useIptorTranslation } from '@iptor/base';
import { Grid2 as Grid } from '@mui/material';
import { Control, Controller, FieldErrors, UseFormGetValues } from 'react-hook-form';
import { Order } from '../../../constants';
import { formatDate, formatDateToPayloadFormat } from '../../../utils/formatDate';
import { DispatchAddressDropdown, MannersOfTransportDropdown, TermsOfDeliveryDropdown } from '@iptor/business';
import { DisplayRulesKey } from '../constants/displayRules';
import dayjs from 'dayjs';

type ShippingInfoProps = {
  getValues?: UseFormGetValues<Partial<Order>>;
  watch?: UseFormGetValues<Partial<Order>>;
  control?: Control<Partial<Order>>;
  errors?: FieldErrors<Partial<Order>>;
  isReadOnlyFromDisplayRulesByKey: (key: DisplayRulesKey) => boolean;
  businessPartner: string;
};

const ShippingInfo = ({
  control,
  getValues,
  errors,
  isReadOnlyFromDisplayRulesByKey,
  businessPartner,
}: ShippingInfoProps) => {
  const { t } = useIptorTranslation();

  return (
    <Accordion
      headingTitle={t('common:TXT_Shipping_Info')}
      moreInfo={
        <Grid container columnSpacing={3} rowSpacing={2} alignItems="flex-end">
          <Grid size={3}>
            <FormControlLabel
              sx={{ alignItems: 'flex-start', marginLeft: 0 }}
              control={
                <Text id="holdingCompany" fontSize={14} color="textDisabled">
                  {getValues?.('holdingCompany') || ' '}
                </Text>
              }
              readOnly={isReadOnlyFromDisplayRulesByKey('holdingCompany')}
              label={<Text fontWeight={600}>{t('common:TXT_Holding_Company')}</Text>}
              labelPlacement="top"
            />
          </Grid>
          <Grid size={3}>
            <FormControlLabel
              sx={{ alignItems: 'flex-start' }}
              control={
                <Text id="parentCompany" fontSize={14} color="textDisabled">
                  {getValues?.('parentCompany') || ' '}
                </Text>
              }
              label={<Text fontWeight={600}>{t('common:TXT_Parent_Company')}</Text>}
              readOnly={isReadOnlyFromDisplayRulesByKey('parentCompany')}
              labelPlacement="top"
            />
          </Grid>
          <Grid size={3}>
            <Controller
              name="ediCreated"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  readOnly={isReadOnlyFromDisplayRulesByKey('ediCreated')}
                  id={field.name}
                  control={<Checkbox id={field.name} {...field} />}
                  label={t('common:TXT_EDI_Created')}
                />
              )}
            />
          </Grid>
        </Grid>
      }
    >
      <Grid container columnSpacing={3} rowSpacing={2}>
        <Grid size={12}>
          <Text fontSize={18} fontWeight={600}>
            {t('common:TXT_Shipment_method')}
          </Text>
        </Grid>

        <Grid size={12 / 4}>
          <Controller
            name="dispatchTime"
            control={control}
            rules={{
              validate: (value) => !value || dayjs(value).isValid() || 'Invalid date format',
            }}
            render={({ field }) => (
              <DatePicker
                label={t('common:TXT_Dispatch_Date')}
                value={formatDate(getValues?.('dispatchTime'))}
                onChange={(value) => field.onChange(formatDateToPayloadFormat(value))}
                slotProps={{
                  textField: {
                    id: 'dispatchTime',
                  },
                }}
                readOnly={isReadOnlyFromDisplayRulesByKey('dispatchTime')}
              />
            )}
          />
        </Grid>

        <Grid size={12 / 4}>
          <Controller
            name="deliveryScheduleNumber"
            control={control}
            rules={{
              validate: (value) => !value || dayjs(value).isValid() || 'Invalid date format',
            }}
            render={({ field }) => (
              <DatePicker
                label={t('common:TXT_Request_Delivery_Date')}
                value={formatDate(getValues?.('deliveryScheduleNumber'))}
                readOnly={isReadOnlyFromDisplayRulesByKey('deliveryScheduleNumber')}
                slotProps={{
                  textField: {
                    id: 'deliveryScheduleNumber',
                  },
                }}
                onChange={(value) => field.onChange(formatDateToPayloadFormat(value))}
              />
            )}
          />
        </Grid>

        <Grid size={12 / 2}>
          <Controller
            name="dispatchAddress"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <DispatchAddressDropdown
                businessPartner={businessPartner}
                value={field.value}
                onChange={(value) => field.onChange(value)}
                id={field.name}
                color={error ? 'error' : 'primary'}
                helperText={error ? error.message : ''}
                label={t('common:TXT_Address')}
                readOnly={isReadOnlyFromDisplayRulesByKey('dispatchAddress')}
                fullWidth
              />
            )}
          />
        </Grid>

        <Grid size={12 / 2}>
          <Controller
            name="termsOfDelivery"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TermsOfDeliveryDropdown
                onChange={(value) => field.onChange(value)}
                value={field.value}
                fullWidth
                id={field.name}
                color={error ? 'error' : 'primary'}
                helperText={error ? error.message : ''}
                label={t('common:TXT_Terms_Of_Delivery')}
                readOnly={isReadOnlyFromDisplayRulesByKey('termsOfDelivery')}
              />
            )}
          />
        </Grid>

        <Grid size={12 / 2}>
          <Controller
            name="mannerOfTransport"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <MannersOfTransportDropdown
                onChange={(value) => field.onChange(value)}
                value={field.value}
                fullWidth
                id={field.name}
                color={error ? 'error' : 'primary'}
                helperText={error ? error.message : ''}
                label={t('common:TXT_Manner_Of_Transport')}
                readOnly={isReadOnlyFromDisplayRulesByKey('mannerOfTransport')}
              />
            )}
          />
        </Grid>

        <Grid size={12 / 2}>
          <Controller
            name="shipmentPreparation"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('common:TXT_Shipment_Mark')}
                fullWidth
                error={!!errors?.shipmentPreparation}
                helperText={errors?.shipmentPreparation?.message}
                readOnly={isReadOnlyFromDisplayRulesByKey('shipmentPreparation')}
                id={field.name}
              />
            )}
          />
        </Grid>
      </Grid>
    </Accordion>
  );
};

export default ShippingInfo;
